import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { Download, FileDown, Mail, Send, ExternalLink } from 'lucide-react';
import { SortableTableHeader, sortData } from '../../components/SortableTableHeader';

export function ReportFooter() {
  const year = new Date().getFullYear();
  return (
    <div className="mt-6 pt-4 border-t border-muted flex items-center justify-between" data-testid="report-footer">
      <p className="text-[10px] text-muted-foreground">&copy; {year} Eyvo. All rights reserved.</p>
      <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-1 text-[10px] text-primary/70 hover:text-primary transition-colors font-medium"
        data-testid="powered-by-eyvo">
        Powered by Eyvo <ExternalLink className="h-2.5 w-2.5" />
      </a>
    </div>
  );
}

export function EmailReportDialog({ open, onClose, reportId, filters, onGeneratePdf, reportLabel }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email.trim() || !email.includes('@')) { toast.error('Please enter a valid email address'); return; }
    if (!onGeneratePdf) { toast.error('PDF generation not available for this report'); return; }
    setSending(true);
    try {
      const doc = onGeneratePdf();
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const r = await api.post('/api/inventory/mgmt-reports/email-report', {
        report_id: reportId,
        email: email.trim(),
        filters: filters || {},
        pdf_base64: pdfBase64,
        report_label: reportLabel || reportId,
      });
      toast.success(r.data.message);
      setEmail('');
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to send email'));
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="email-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" /> Email Report
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-muted-foreground">
            The report will be generated as a branded PDF and sent as an attachment to the email address you enter.
          </p>
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Recipient Email</Label>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="colleague@company.com"
              className="h-9 text-sm"
              data-testid="email-report-input"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={handleSend} disabled={sending} size="sm" data-testid="email-report-send">
              <Send className="h-3.5 w-3.5 mr-1.5" /> {sending ? 'Sending...' : 'Send Report'}
            </Button>
            <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReportToolbar({ onCSV, onPDF, onEmailPdf, loading, children, reportId, filters, reportLabel }) {
  const [emailOpen, setEmailOpen] = useState(false);
  return (
    <>
      <div className="flex items-end gap-3 mb-4 p-3 bg-muted/30 rounded-xl border flex-wrap">
        {children}
        <div className="flex gap-2 ml-auto">
          {onCSV && <Button size="sm" variant="outline" onClick={onCSV} disabled={loading} data-testid="report-export-csv">
            <Download className="h-3.5 w-3.5 mr-1" /> CSV
          </Button>}
          {onPDF && <Button size="sm" variant="outline" onClick={onPDF} disabled={loading} data-testid="report-export-pdf" className="border-red-200 text-red-700 hover:bg-red-50">
            <FileDown className="h-3.5 w-3.5 mr-1" /> PDF
          </Button>}
          {reportId && (
            <Button size="sm" variant="outline" onClick={() => setEmailOpen(true)} disabled={loading} data-testid="report-email-btn"
              className="border-blue-200 text-blue-700 hover:bg-blue-50">
              <Mail className="h-3.5 w-3.5 mr-1" /> Email
            </Button>
          )}
        </div>
      </div>
      {reportId && <EmailReportDialog open={emailOpen} onClose={() => setEmailOpen(false)} reportId={reportId} filters={filters}
        onGeneratePdf={onEmailPdf} reportLabel={reportLabel} />}
    </>
  );
}

export function SummaryCard({ label, value, sub, color = 'text-foreground' }) {
  return (
    <div className="p-3 border rounded-lg bg-white">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function FilterInput({ label, value, onChange, type = 'text', placeholder, className = 'w-36' }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-semibold">{label}</Label>
      <Input type={type} value={value} onChange={e => onChange(e.target.value)} className={`h-7 text-xs ${className}`} placeholder={placeholder || label} data-testid={`filter-${label.toLowerCase().replace(/\s/g, '-')}`} />
    </div>
  );
}

export function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-semibold">{label}</Label>
      <select value={value} onChange={e => onChange(e.target.value)} className="h-7 text-xs border rounded px-2 w-36 bg-white" data-testid={`filter-${label.toLowerCase().replace(/\s/g, '-')}`}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function FilterToggle({ label, value, onChange }) {
  return (
    <div className="space-y-1 flex items-end">
      <label className="flex items-center gap-1.5 cursor-pointer h-7">
        <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} className="h-3.5 w-3.5 rounded" data-testid={`filter-${label.toLowerCase().replace(/\s/g, '-')}`} />
        <span className="text-[10px] font-semibold">{label}</span>
      </label>
    </div>
  );
}

export function DataTable({ rows, columns }) {
  const [tblSort, setTblSort] = useState({ field: '', dir: 'asc' });
  const handleTblSort = (field, dir) => setTblSort({ field, dir });
  if (!rows?.length) return <div className="text-center py-6 text-muted-foreground text-xs border rounded-xl">No data</div>;
  const sorted = tblSort.field ? sortData(rows, tblSort.field, tblSort.dir) : rows;
  return (
    <div className="border rounded-xl overflow-hidden">
      <table className="w-full text-xs">
        <thead><tr className="bg-muted/50 border-b">
          {columns.map(c => (
            <SortableTableHeader key={c.key} label={c.label} field={c.key} sortState={tblSort} onSort={handleTblSort} className={c.align === 'right' ? 'text-right' : ''} />
          ))}
        </tr></thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={i} className="border-b hover:bg-accent/20 transition-colors">
              {columns.map(c => (
                <td key={c.key} className={`px-3 py-1.5 ${c.align === 'right' ? 'text-right' : ''} ${c.className || ''}`}>
                  {c.fmt ? c.fmt(row[c.key], row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
